<template>
  <div class="row">
    <div class="col-12">
      <div class="pane-header">
        <span class="icon"><icon-colored-summary /></span>
        <span class="text">{{ capitalize($t('messages.overallSummary')) }}</span>
        <div
          v-if="props.noAudienceUnitsCount > 0 && props.unitsCount !== 0"
          class="actions">
          <entity-actions
            class="actions"
            :actions="actions"
            @changed="dispatchAction" />
        </div>
      </div>
    </div>
    <div class="col-12">
      <div class="pane-content pad-top notes-container">
        <div class="row">
          <div class="col-12">
            <div class="summary-title">
              <KPI
                :label="$t('names.screen', { n: 2 })"
                :value="countFormatted"
                :icon="IconColoredUnit"
                :big-icon="true" />
              <KPI
                :label="$t('messages.averageAudienceConcentration')"
                :value="props.averageAudienceConcentration"
                :icon="IconColoredDemographic"
                :big-icon="true" />
              <KPI
                v-if="props.noAudienceUnitsCount > 0 && props.unitsCount !== 0"
                :label="$t('messages.screenWithNoAudienceConcentration', props.noAudienceUnitsCount === 1 ? 1 : 2)"
                :value="invalidCountFormatted"
                :icon="IconColoredWarning"
                :big-icon="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import numbro from 'numbro';
  import { capitalize, computed, markRaw } from 'vue';

  import EntityActions from '@/common/components/controls/EntityActions.vue';
  import {
    IconColoredDemographic,
    IconColoredSummary,
    IconColoredUnit,
    IconColoredWarning,
  } from '@/common/icons/colored';
  import { IconEdit } from '@/common/icons/monochrome';
  import KPI from '@/org-app/components/controls/KPI.vue';
  import i18n from '@/vueI18n';

  interface Props {
    averageAudienceConcentration: string;
    noAudienceUnitsCount: number;
    unitsCount: number;
  }
  interface Emits {
    (e: 'updateUnitIDs'): void;
    (e: 'downloadUnits'): void;
  }
  const emit = defineEmits<Emits>();
  const props = defineProps<Props>();
  const MAX_INVALID_COUNT = 10000;

  //
  // Computed
  //
  const actions = computed(() => [
    {
      type: 'downloadNoAudienceConcentrationScreen',
      name: capitalize(i18n.global.t('actions.downloadScreensWithNoAudienceConcentration')),
      icon: markRaw(IconEdit),
    },
    {
      type: 'removeNoAudienceConcentrationScreen',
      name: capitalize(i18n.global.t('actions.removeScreensWithNoAudienceConcentration')),
      icon: markRaw(IconEdit),
    },
  ]);
  const invalidCountFormatted = computed(() => {
    return `${props.noAudienceUnitsCount > MAX_INVALID_COUNT ? '>' : ''}${numbro(props.noAudienceUnitsCount).format('0,00')}`;
  });
  const countFormatted = computed(() => {
    return numbro(props.unitsCount).format('0,00');
  });

  //
  // Methods
  //
  function dispatchAction(action) {
    switch (action.type) {
      case 'downloadNoAudienceConcentrationScreen':
        emit('downloadUnits');
        break;
      case 'removeNoAudienceConcentrationScreen':
        emit('updateUnitIDs');
        break;
      default:
        break;
    }
  }
</script>

<style scoped lang="scss">
  .pane-header {
    display: flex;
    align-items: center;

    .actions {
      margin-left: auto;
    }
  }

  .summary-title {
    display: flex;
  }
</style>
